import { FC, useEffect, useState } from 'react';
import { useEditorData, useEditorHistory } from '../../lib/FabricJs';
import { useHandleHistory } from '../../lib/FabricJs/hooks/useHandleHistory';
import { IFRAME_WIDGETS } from '../../config';
import { isQrCode } from '../../lib/FabricJs/utils/qrObject';
import {
  getSlideBounds,
  moveBoundsTo,
  rotateInPlace,
} from '../../utils/objectGeometry';
import NumberField from './NumberField';
import Section, { ButtonGrid } from './Section';

const LIVE_EVENTS = [
  'object:moving',
  'object:scaling',
  'object:rotating',
  'object:modified',
];

const PositionSize: FC = () => {
  const { canvas } = useEditorData();
  const { history } = useEditorHistory();
  const { handleHistory } = useHandleHistory();
  const [, setVersion] = useState(0);

  // Show values while the object is dragged, resized or rotated on the
  // canvas, and after undo/redo.
  useEffect(() => {
    if (!canvas) return;
    const refresh = () => setVersion((version) => version + 1);
    LIVE_EVENTS.forEach((name) => canvas.on(name, refresh));
    return () => LIVE_EVENTS.forEach((name) => canvas.off(name, refresh));
  }, [canvas]);
  useEffect(() => {
    const frame = requestAnimationFrame(() =>
      setVersion((version) => version + 1),
    );
    return () => cancelAnimationFrame(frame);
  }, [history.present]);

  // Edits apply to the object shown here, even if the selection changes
  // before a field commits (clicking the canvas blurs the field after
  // Fabric has already selected something else).
  const object = canvas?.getActiveObject();
  if (!canvas || !object) return null;

  const isMultiple = object.type === 'activeSelection';
  // Widgets are HTML laid over the canvas: they cannot be shown rotated or
  // translucent.
  const isWidget = Object.values(IFRAME_WIDGETS).includes(object.data?.type);
  const bounds = getSlideBounds(object);
  const apply = (change: () => void) => {
    change();
    canvas.requestRenderAll();
    handleHistory(canvas);
  };

  const setSize = (dimension: 'width' | 'height', value: number) =>
    apply(() => {
      const base = object[dimension] || 1;
      const scale = Math.max(value, 1) / base;
      if (isQrCode(object)) {
        // Stretching would distort the code.
        object.set({ scaleX: scale, scaleY: scale });
      } else {
        object.set(
          dimension === 'width' ? { scaleX: scale } : { scaleY: scale },
        );
      }
      object.setCoords();
    });

  const setOpacity = (percent: number) =>
    apply(() => {
      const targets = isMultiple ? canvas.getActiveObjects() : [object];
      targets.forEach((target) => target.set({ opacity: percent / 100 }));
    });

  const opacity = isMultiple ? null : Math.round((object.opacity ?? 1) * 100);

  return (
    <Section title="Position & size">
      <ButtonGrid columns={2}>
        <NumberField
          label="X position"
          prefix="X"
          value={bounds.left}
          disabled={!!object.lockMovementX}
          onCommit={(left) => apply(() => moveBoundsTo(object, { left }))}
        />
        <NumberField
          label="Y position"
          prefix="Y"
          value={bounds.top}
          disabled={!!object.lockMovementY}
          onCommit={(top) => apply(() => moveBoundsTo(object, { top }))}
        />
        <NumberField
          label="Width"
          prefix="W"
          value={object.getScaledWidth()}
          disabled={isMultiple || !!object.lockScalingX}
          min={1}
          onCommit={(width) => setSize('width', width)}
        />
        <NumberField
          label="Height"
          prefix="H"
          value={object.getScaledHeight()}
          disabled={isMultiple || !!object.lockScalingY}
          min={1}
          onCommit={(height) => setSize('height', height)}
        />
        <NumberField
          label="Rotation in degrees"
          prefix="°"
          value={isMultiple ? null : object.angle ?? 0}
          disabled={isMultiple || isWidget || !!object.lockRotation}
          onCommit={(angle) => apply(() => rotateInPlace(object, angle))}
        />
        <NumberField
          label="Opacity in percent"
          prefix="%"
          value={opacity}
          disabled={isWidget}
          min={0}
          max={100}
          onCommit={setOpacity}
        />
      </ButtonGrid>
    </Section>
  );
};

export default PositionSize;
